import React, { useState } from "react";
import {
  FiSearch,
  FiBox,
  FiUser,
  FiShoppingCart,
  FiTag,
  FiGrid,
  FiHardDrive,
  FiList,
  FiClipboard,
  FiLogOut,
  FiMenu,
  FiChevronRight,
} from "react-icons/fi";

const Sidebar = ({ isOpen, toggleSidebar, onMenuItemClick }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const menuSections = [
    {
      title: "OPERATIONS",
      items: [
        { name: "Quick Search", icon: <FiSearch />, color: "text-sky-300" },
        { name: "Product", icon: <FiBox />, color: "text-amber-300" },
        { name: "Ledger", icon: <FiUser />, color: "text-blue-300" },
        { name: "Purchase", icon: <FiShoppingCart />, color: "text-teal-300" },
        { name: "Sales", icon: <FiTag />, color: "text-indigo-300" },
        { name: "Dash Board", icon: <FiGrid />, color: "text-purple-300" },
      ],
    },
    {
      title: "MANAGEMENT",
      items: [
        { name: "Price List", icon: <FiList />, color: "text-cyan-300" },
        { name: "Jobcard Home", icon: <FiClipboard />, color: "text-emerald-300" },
        { name: "Backup", icon: <FiHardDrive />, color: "text-blue-300" },
      ],
    },
    {
      title: "SYSTEM",
      items: [
        { name: "Exit", icon: <FiLogOut />, color: "text-rose-400", isDanger: true },
      ],
    },
  ];

  // Flattened items for indexed access
  const allItems = menuSections.flatMap((s) => s.items);

  return (
    <>
      {/* Transparent click-outside dismisser (elevated to z-[9999990] above all components) */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[9999990] bg-transparent"
          onClick={toggleSidebar}
        />
      )}

      {/* In-flow fixed rail placeholder (w-14) elevated above modals when open */}
      <div className={`w-14 h-full shrink-0 relative ${isOpen ? "z-[9999999]" : "z-40"}`}>
        {/* Floating Smart Sidebar Drawer - elevated above all opened components */}
        <div
          className={`absolute top-0 left-0 h-full bg-gradient-to-b from-[#0b1b4f] via-[#0d2263] to-[#071338] text-white flex flex-col transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            isOpen
              ? "w-60 shadow-[0_10px_35px_rgba(0,0,0,0.55)] border-r border-white/15 z-[9999999]"
              : "w-14 overflow-visible shadow-lg border-r border-white/10 z-40"
          } select-none`}
        >
          {/* Header & Toggle Button */}
          <div
            className="flex items-center justify-between h-[52px] px-3.5 mt-1 cursor-pointer border-b border-white/10 hover:bg-white/5 transition-all duration-200 group"
            onClick={toggleSidebar}
            title={isOpen ? "Collapse Menu" : "Expand Menu"}
          >
            <div className="flex items-center min-w-0">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-white/90 group-hover:bg-blue-500/30 group-hover:text-white transition-all duration-200 shrink-0">
                <FiMenu
                  className={`text-lg transition-transform duration-300 ease-out ${
                    isOpen ? "rotate-90 text-blue-300" : "rotate-0"
                  }`}
                />
              </div>
              <div
                className={`ml-3 overflow-hidden transition-all duration-300 ${
                  isOpen ? "opacity-100 max-w-[140px]" : "opacity-0 max-w-0 pointer-events-none"
                }`}
              >
                <div className="font-bold text-xs tracking-widest text-white uppercase">
                  Menu
                </div>
              </div>
            </div>

            {isOpen && (
              <div className="text-white/40 group-hover:text-white/80 transition-colors mr-1">
                <FiChevronRight className="rotate-180 text-sm" />
              </div>
            )}
          </div>

          {/* Menu Sections & Items */}
          <div
            className={`flex-1 py-2 ${
              isOpen ? "overflow-y-auto overflow-x-hidden" : "overflow-visible"
            } no-scrollbar space-y-3`}
          >
            {menuSections.map((section) => (
              <div key={section.title} className="space-y-0.5">
                {/* Section Header (visible only when open) */}
                <div
                  className={`px-4 pt-1 pb-0.5 text-[10px] font-bold tracking-wider text-blue-300/60 uppercase transition-all duration-300 overflow-hidden ${
                    isOpen ? "max-h-6 opacity-100" : "max-h-0 opacity-0 py-0"
                  }`}
                >
                  {section.title}
                </div>

                {section.items.map((item) => {
                  const flatIndex = allItems.findIndex((it) => it.name === item.name);
                  const isHovered = hoveredIndex === flatIndex;

                  return (
                    <div
                      key={item.name}
                      className={`relative flex items-center mx-1.5 px-2 py-1.5 rounded-lg cursor-pointer transition-all duration-200 group ${
                        item.isDanger
                          ? "hover:bg-rose-500/15"
                          : "hover:bg-white/10"
                      }`}
                      onMouseEnter={() => setHoveredIndex(flatIndex)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      onClick={() => {
                        if (isOpen) toggleSidebar();
                        onMenuItemClick && onMenuItemClick(item.name);
                      }}
                    >
                      {/* Active indicator bar */}
                      <div
                        className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 rounded-r transition-all duration-200 ${
                          item.isDanger
                            ? "bg-rose-500 group-hover:h-5 h-0"
                            : "bg-gradient-to-b from-blue-400 to-indigo-400 group-hover:h-5 h-0"
                        }`}
                      />

                      {/* Icon container */}
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center text-[1.05rem] transition-all duration-200 shrink-0 ${
                          item.isDanger
                            ? "bg-rose-500/10 border border-rose-500/20 text-rose-300 group-hover:bg-rose-500/25 group-hover:border-rose-400/50 group-hover:scale-110 group-hover:shadow-[0_0_12px_rgba(244,63,94,0.35)]"
                            : "bg-white/5 border border-white/15 " +
                              item.color +
                              " group-hover:text-white group-hover:bg-blue-500/25 group-hover:border-blue-400/40 group-hover:scale-110 group-hover:shadow-[0_0_12px_rgba(59,130,246,0.35)]"
                        }`}
                      >
                        {item.icon}
                      </div>

                      {/* Label with smart staggered entrance transition */}
                      <span
                        style={{
                          transitionDelay: isOpen ? `${flatIndex * 20}ms` : "0ms",
                        }}
                        className={`text-[12.5px] font-medium tracking-wide whitespace-nowrap overflow-hidden transition-all duration-300 ${
                          item.isDanger
                            ? "text-rose-200 group-hover:text-white"
                            : "text-white/85 group-hover:text-white"
                        } ${
                          isOpen
                            ? "ml-3 opacity-100 translate-x-0"
                            : "ml-0 opacity-0 -translate-x-3 pointer-events-none"
                        }`}
                      >
                        {item.name}
                      </span>

                      {/* Smart Floating Tooltip when collapsed */}
                      {!isOpen && isHovered && (
                        <div
                          style={{
                            left: "58px",
                            top: "50%",
                          }}
                          className="absolute z-[9999999] pointer-events-none flex items-center smart-tooltip-in"
                        >
                          <div
                            className={`w-2 h-2 rotate-45 -mr-1 z-10 shrink-0 ${
                              item.isDanger
                                ? "bg-[#250814] border-l border-b border-rose-500/60"
                                : "bg-[#0c1c4f] border-l border-b border-blue-400/60"
                            }`}
                          />
                          <div
                            className={`text-[12px] font-semibold tracking-wide py-1.5 px-3 rounded-md shadow-2xl border whitespace-nowrap drop-shadow-md ${
                              item.isDanger
                                ? "bg-[#250814] border-rose-500/60 text-rose-100 shadow-rose-950/60"
                                : "bg-[#0c1c4f] border-blue-400/60 text-white shadow-black/80"
                            }`}
                          >
                            {item.name}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Smart Footer - System & Connection Status */}
          <div
            className={`p-3 border-t border-white/10 transition-all duration-300 ${
              isOpen ? "opacity-100" : "opacity-0 pointer-events-none py-1"
            }`}
          >
            <div className="flex items-center justify-between text-[11px] text-white/50 font-semibold tracking-wider uppercase">
              <span>SherGold</span>
              <span className="text-[10px] text-emerald-400 font-normal lowercase flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                online
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
